import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

// Field configuration types
export type ProjectFieldKey = 
  | 'projectName'
  | 'projectCode'
  | 'description'
  | 'status'
  | 'priority'
  | 'type'
  | 'startDate'
  | 'endDate'
  | 'duration'
  | 'projectManager'
  | 'clients'
  | 'budget'
  | 'tags';

export type DurationUnit = 'days' | 'weeks' | 'months' | 'years';

export interface FieldConfig {
  key: ProjectFieldKey;
  label: string;
  isMandatory: boolean;
  isAlwaysMandatory?: boolean; // Cannot be changed by user
  section: 'essential' | 'general' | 'timeline' | 'management';
  order: number;
  autoGenerateCode?: boolean; // For projectCode field - auto-generate from project name
}

export interface TimelineSettings {
  defaultDurationUnit: DurationUnit;
  defaultToEndDate: boolean; // true = End Date mode, false = Duration mode
  allowModeSwitch: boolean; // true = user can switch, false = read-only
}

interface FieldConfigContextType {
  fieldConfigs: FieldConfig[];
  mandatoryFields: ProjectFieldKey[];
  timelineSettings: TimelineSettings;
  updateFieldConfig: (key: ProjectFieldKey, updates: Partial<FieldConfig>) => void;
  updateTimelineSettings: (updates: Partial<TimelineSettings>) => void;
  getMandatoryFieldsBySection: (section: FieldConfig['section']) => FieldConfig[];
  getFieldConfig: (key: ProjectFieldKey) => FieldConfig | undefined;
  generateProjectCode: (projectName: string) => string;
}

const FieldConfigContext = createContext<FieldConfigContextType | undefined>(undefined);

// Default field configurations
const DEFAULT_FIELD_CONFIGS: FieldConfig[] = [
  // Essential Fields (Always Mandatory)
  { key: 'projectName', label: 'Project Name', isMandatory: true, isAlwaysMandatory: true, section: 'essential', order: 1 },
  { key: 'projectCode', label: 'Project Code', isMandatory: true, isAlwaysMandatory: true, section: 'essential', order: 2 },
  
  // General Information
  { key: 'description', label: 'Description', isMandatory: false, section: 'general', order: 3 },
  { key: 'status', label: 'Status', isMandatory: false, section: 'general', order: 4 },
  { key: 'priority', label: 'Priority', isMandatory: false, section: 'general', order: 5 },
  { key: 'type', label: 'Type', isMandatory: false, section: 'general', order: 6 },
  { key: 'startDate', label: 'Start Date', isMandatory: false, section: 'timeline', order: 7 },
  { key: 'endDate', label: 'End Date', isMandatory: false, section: 'timeline', order: 8 },
  { key: 'duration', label: 'Duration', isMandatory: false, section: 'timeline', order: 9 },
  { key: 'projectManager', label: 'Project Manager', isMandatory: false, section: 'management', order: 10 },
  { key: 'clients', label: 'Clients', isMandatory: false, section: 'management', order: 11 },
  { key: 'budget', label: 'Budget', isMandatory: false, section: 'management', order: 12 },
  { key: 'tags', label: 'Tags', isMandatory: false, section: 'management', order: 13 },
];

// Default timeline settings
const DEFAULT_TIMELINE_SETTINGS: TimelineSettings = {
  defaultDurationUnit: 'days',
  defaultToEndDate: true, // Default to End Date mode
  allowModeSwitch: true, // Allow users to switch by default
};

export function FieldConfigProvider({ children }: { children: ReactNode }) {
  // Load from localStorage or use defaults
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>(() => {
    const saved = localStorage.getItem('projectFieldConfigs');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Migrate: ensure projectName and projectCode are in 'essential' section and always mandatory
        let migrated = parsed.map((config: FieldConfig) => {
          if (config.key === 'projectName') {
            return { ...config, section: 'essential' as const, isMandatory: true, isAlwaysMandatory: true };
          }
          if (config.key === 'projectCode') {
            return { ...config, section: 'essential' as const, isMandatory: true, isAlwaysMandatory: true };
          }
          return config;
        });
        
        // Migrate: add duration field if it doesn't exist
        const hasDuration = migrated.some((config: FieldConfig) => config.key === 'duration');
        if (!hasDuration) {
          const durationConfig = DEFAULT_FIELD_CONFIGS.find(c => c.key === 'duration');
          if (durationConfig) {
            migrated.push(durationConfig);
          }
        }
        
        return migrated;
      } catch {
        return DEFAULT_FIELD_CONFIGS;
      }
    }
    return DEFAULT_FIELD_CONFIGS;
  });

  // Load timeline settings from localStorage or use defaults
  const [timelineSettings, setTimelineSettings] = useState<TimelineSettings>(() => {
    const saved = localStorage.getItem('projectTimelineSettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return DEFAULT_TIMELINE_SETTINGS;
      }
    }
    return DEFAULT_TIMELINE_SETTINGS;
  });

  // Save to localStorage whenever configs change
  useEffect(() => {
    localStorage.setItem('projectFieldConfigs', JSON.stringify(fieldConfigs));
  }, [fieldConfigs]);

  // Save to localStorage whenever timeline settings change
  useEffect(() => {
    localStorage.setItem('projectTimelineSettings', JSON.stringify(timelineSettings));
  }, [timelineSettings]);

  const mandatoryFields = fieldConfigs
    .filter(config => config.isMandatory)
    .map(config => config.key);

  const updateFieldConfig = (key: ProjectFieldKey, updates: Partial<FieldConfig>) => {
    setFieldConfigs(prev =>
      prev.map(config => {
        if (config.key === key) {
          // Prevent changing mandatory status for always mandatory fields
          if (config.isAlwaysMandatory && 'isMandatory' in updates) {
            return config;
          }
          return { ...config, ...updates };
        }
        return config;
      })
    );
  };

  const updateTimelineSettings = (updates: Partial<TimelineSettings>) => {
    setTimelineSettings(prev => ({ ...prev, ...updates }));
  };

  const getMandatoryFieldsBySection = (section: FieldConfig['section']) => {
    return fieldConfigs
      .filter(config => config.isMandatory && config.section === section)
      .sort((a, b) => a.order - b.order);
  };

  const getFieldConfig = (key: ProjectFieldKey) => {
    return fieldConfigs.find(config => config.key === key);
  };

  const generateProjectCode = (projectName: string): string => {
    if (!projectName) return '';
    
    // Convert to uppercase and remove special characters
    const cleaned = projectName
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .trim();
    
    // Split into words and take first letter of each word (up to 3 words)
    const words = cleaned.split(/\s+/).filter(word => word.length > 0);
    const acronym = words.slice(0, 3).map(word => word[0]).join('');
    
    // Get current year
    const year = new Date().getFullYear();
    
    // Generate a simple sequential number (in real implementation, this would check for duplicates)
    const sequence = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    
    return `${acronym}-${year}-${sequence}`;
  };

  return (
    <FieldConfigContext.Provider
      value={{
        fieldConfigs,
        mandatoryFields,
        timelineSettings,
        updateFieldConfig,
        updateTimelineSettings,
        getMandatoryFieldsBySection,
        getFieldConfig,
        generateProjectCode,
      }}
    >
      {children}
    </FieldConfigContext.Provider>
  );
}

export function useFieldConfig() {
  const context = useContext(FieldConfigContext);
  if (context === undefined) {
    throw new Error('useFieldConfig must be used within a FieldConfigProvider');
  }
  return context;
}
